<script setup lang="ts">
import { onMounted, onUnmounted, useTemplateRef, watch } from 'vue';
import { Popover } from 'bootstrap';

const infoRef = useTemplateRef<HTMLElement>('infoRef');

const props = withDefaults(
	defineProps<{
		/** Heading of the popover */
		title?: string;
		/** Body of the popover */
		text: string;
		/** Font Awesome class of the trigger icon */
		icon?: string;
		/**
		 * Renders title and text as HTML. Trusted markup only: Bootstrap's sanitizer strips
		 * scripts and handlers, but anything a user typed must never be passed in with it.
		 */
		html?: boolean;
	}>(),
	{
		icon: 'fa-info-circle',
		html: false,
	},
);

let popover: Popover | null = null;

onMounted(() => {
	if (!infoRef.value) return;
	popover = new Popover(infoRef.value, {
		container: 'body',
		placement: 'auto',
		trigger: 'hover',
		html: props.html ?? false,
		title: props.title ?? '',
		content: props.text,
		//boundary: 'viewport', // optionally constrain to viewport if needed
	});
});

// Bootstrap copies title/content into its own config on creation, so prop
// changes (e.g. when table rows are patched in place) must be pushed manually
watch(
	() => [props.title, props.text],
	() => {
		popover?.setContent({
			'.popover-header': props.title ?? '',
			'.popover-body': props.text,
		});
	},
);

onUnmounted(() => {
	popover?.dispose();
	popover = null;
});
</script>

<template>
	<!-- The gap before the icon belongs to the default icon, not to the wrapper:
	     a custom slot (avatar, legend entry) brings its own spacing. -->
	<span ref="infoRef" class="d-inline-flex align-items-center" role="button" tabindex="0">
		<slot><i class="fas small ms-1" :class="props.icon" /></slot>
	</span>
</template>
